import * as R from 'ramda'
// React
import { useCallback } from 'react'
import { LIMITS } from '@weblite/permission'
// Material
import { Box, Button, Card, Divider, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Gender, Location } from '@weblite/sahab-profile-action/profile'
import { useActiveSectionsTop, useHistoryCustomBack } from 'weblite-router'
import { ReactJSXElement } from '@emotion/react/types/jsx-namespace'
// Common
import { Label } from '$common/label'
import { TextInput } from '$common/input'
import { nothing } from '$func/utils'
import { AuthHeader } from '$common/auth-header/auth-header'
import { SelectButton } from '$common/buttons'
import { showSelectCounty, showSelectGender } from '$comp/modals/select-menu'
import { dateFormatter } from '$func/time'
import { showBirthdatePickerModal } from '$comp/modals/date-picker'
import { editProfile, getReadableLocation } from '$logic/profile'
import { SelectWithChips } from '$common/select-with-chips/select-with-chips'
import { updateClientUser } from '$logic/client-user'
import { joinHallViaLink } from '$logic/subscription'
import { showTasksLimitSnackbar } from '$comp/snackbars'
import { BottomDrawer } from '$common/bottom-drawer'
import { closeProfileCompletion } from '$logic/router'
import { usePrevious } from '$common/react-hooks/use-previous'
import { useIsDesktopSize } from '$common/react-hooks/layout'
import { useFormData } from '$hooks/use-form-data'
import { t } from '$i18n'
import {
  SPECIALITIES_OPTIONS,
  MAP_SPECIALITIES_TO_CHANNEL_INVITATION_LINK,
  PUBLIC_CHANNELS_INVITE_LINKS,
} from './profile-completion.data'

const { userConstraints } = LIMITS
const MAX_SPECIALITIES_COUNT = 2

type ProfileCompletionData = {
  fatherName: string
  location: Location | undefined
  gender: Gender | undefined
  birthDate: string
  specialities: string[]
  nationalId: string
  nationalIdError: string
}

const initialValue = {
  fatherName: '',
  location: undefined,
  gender: undefined,
  birthDate: '',
  specialities: [],
  nationalId: '',
  nationalIdError: '',
} as ProfileCompletionData

export function ProfileCompletionModal() {
  const { items, setItem } = useFormData<ProfileCompletionData>(initialValue)

  const {
    fatherName,
    birthDate,
    location,
    gender,
    specialities,
    nationalId,
    nationalIdError,
  } = items

  const onSpecialityClick = useCallback(
    (speciality: string) => {
      if (specialities.includes(speciality)) {
        setItem('specialities', R.without([speciality], specialities))
      } else {
        if (specialities.length === MAX_SPECIALITIES_COUNT) {
          showTasksLimitSnackbar()
          return
        }
        setItem('specialities', specialities.concat(speciality))
      }
    },
    [specialities],
  )

  const onSignUpClick = async () => {
    const res = await updateClientUser({ nationalId })
    if (res?.error?.code) {
      setItem('nationalIdError', t(`completeProfile.errors.${res.error.code}`))
      return
    }

    await editProfile({
      gender,
      birthDate,
      location,
      fatherName,
      tasks: specialities,
    })

    analite('SUBMIT_ADDITIONAL_REGISTRATION')
    await Promise.all(
      specialities.map(async speciality => {
        const target = MAP_SPECIALITIES_TO_CHANNEL_INVITATION_LINK[speciality]
        if (target) await joinHallViaLink(target)
      }),
    )
    await Promise.all(
      PUBLIC_CHANNELS_INVITE_LINKS.map(async channelInviteLink => {
        await joinHallViaLink(channelInviteLink)
      }),
    )
    closeProfileCompletion()
  }

  useHistoryCustomBack(() => {}, [])

  const activeSection = useActiveSectionsTop()
  const prevActiveSection = usePrevious(activeSection)

  const isOpen =
    activeSection === 'sjf' ||
    (prevActiveSection === 'sjf' && activeSection === 'overlay')

  return (
    <AuthContainer isOpen={isOpen}>
      <AuthHeader>
        <Typography variant="headerMedium">
          {t('completeProfile.title')}
        </Typography>
      </AuthHeader>
      <Divider />
      <Box sx={{ marginTop: '20px', marginBottom: 'auto' }}>
        <Label title={t('completeProfile.fatherName')} required />
        <TextInput
          placeholder={t('completeProfile.enterFatherName')}
          value={fatherName}
          onChange={newName => setItem('fatherName', newName)}
          maxLength={userConstraints.firstname.maxLength}
        />
        <Label title={t('completeProfile.nationalCode')} required />
        <TextInput
          placeholder={t('completeProfile.enterNationalCode')}
          value={nationalId}
          onChange={newNationalId => setItem('nationalId', newNationalId)}
          hasError={!!nationalIdError}
          errorMessage={nationalIdError}
          inputProps={{ inputMode: 'numeric' }}
          maxLength={userConstraints.firstname.maxLength}
        />

        <Label title={t('user.location')} required />
        <SelectButton
          value={location ? getReadableLocation(location) : undefined}
          onClick={() =>
            showSelectCounty(newLocation => setItem('location', newLocation))
          }
        />

        <Label title={t('myAccountEdit.birthDate')} required />
        <SelectButton
          value={
            birthDate ? dateFormatter.format(new Date(birthDate)) : undefined
          }
          onClick={() =>
            showBirthdatePickerModal(
              date =>
                // NOTE: Format date to yyyy-mm-dd
                setItem('birthDate', date.toISOString().split('T')[0]),
              new Date(birthDate),
            )
          }
        />

        <Label title={t('myAccountEdit.gender')} required />
        <SelectButton
          value={
            gender
              ? gender === Gender.MALE
                ? t('common.male')
                : t('common.female')
              : null
          }
          onClick={() =>
            showSelectGender(newGender => setItem('gender', newGender))
          }
        />

        <Label title={t('completeProfile.specialities')} required />
        <SelectWithChips
          options={SPECIALITIES_OPTIONS}
          value={specialities}
          onChange={onSpecialityClick}
          getOptionLabel={R.identity}
        />
      </Box>
      <Button
        variant="contained"
        fullWidth
        sx={{ backgroundColor: 'primary500', marginTop: '20px' }}
        size="medium"
        disabled={
          !fatherName ||
          !nationalId ||
          !location ||
          !birthDate ||
          !gender ||
          !specialities.length
        }
        onClick={onSignUpClick}
      >
        {t('common.register')}
      </Button>
    </AuthContainer>
  )
}

interface Prop {
  children: ReactJSXElement[]
  isOpen: boolean
}

const DesktopAuthContainerRoot = styled('div')`
  position: absolute;
  display: flex;
  width: 100%;
  height: 100%;
`

const DesktopCard = styled(Card)`
  border-radius: 16px;
  overflow: auto;
  margin: auto;
  height: calc(100% - 64px);
  max-width: 400px;
  width: 400px;
  padding: 0 16px 32px 16px;
`

function AuthContainer({ children, isOpen }: Prop) {
  const isDesktop = useIsDesktopSize()
  return isDesktop ? ( // TODO: why drawer disappears after select modal items in desktop
    <DesktopAuthContainerRoot>
      <DesktopCard>{children}</DesktopCard>
    </DesktopAuthContainerRoot>
  ) : (
    <BottomDrawer
      isOpen={isOpen}
      variant="permanent"
      onClose={nothing}
      isFullscreen
      sx={{ width: '100%', height: '100%' }}
      paperProps={{ sx: { padding: '0 16px 32px 16px' } }}
    >
      {children}
    </BottomDrawer>
  )
}
